import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, User, Building, Upload, FileUp, Users2, FileText, Download, MessageSquare, Phone, MessagesSquare, Send, FilePlus2, Sparkles, ClipboardList, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatDate, validateIndianMobile } from '../utils/helpers';
import { useGlobalDialogs } from '../contexts/GlobalDialogsContext';
import ClientExposureChart from '../components/ClientExposureChart';
import DocumentExtractDialog from '../components/DocumentExtractDialog';
import EditClientDialog from '../components/EditClientDialog';
import ExtractionAccuracyPanel from '../components/ExtractionAccuracyPanel';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePagedTable, SortableTh, PaginationBar } from '../utils/tableUtils';
import { downloadFile } from '../utils/downloadFile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Clients = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [familyCounts, setFamilyCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [formData, setFormData] = useState({
    name: '', dob: '', date_of_marriage: '', gender: 'Male', pan: '', aadhaar: '',
    address: '', primary_mobile: '', secondary_mobile: '', email: '', nominee: '', client_type: 'Individual',
    financial_holdings: { mutual_funds: 'unknown', loans: 'unknown', fd: 'unknown', rd: 'unknown', post_office: 'unknown', ppf: 'unknown' }
  });

  // Bulk upload
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Manage dialog (Family + KYC + Conversation)
  const [manageOpen, setManageOpen] = useState(false);
  const [manageClient, setManageClient] = useState(null);
  const [familyList, setFamilyList] = useState([]);
  const [kycList, setKycList] = useState([]);
  const [conversation, setConversation] = useState([]);
  const [familyForm, setFamilyForm] = useState({ name: '', relationship: 'Spouse', dob: '', gender: 'Male' });
  const [replyBody, setReplyBody] = useState('');
  const [replyChannel, setReplyChannel] = useState('whatsapp');
  const [replySending, setReplySending] = useState(false);
  const [kycDocType, setKycDocType] = useState('Aadhaar');
  const [kycFile, setKycFile] = useState(null);

  // KYC docs staged for a brand-new client — parity with the header's Add Client dialog.
  const [createKycFiles, setCreateKycFiles] = useState([]);
  const [createKycUploading, setCreateKycUploading] = useState(false);

  // Twilio messaging
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgHistory, setMsgHistory] = useState([]);
  const [docExtractOpen, setDocExtractOpen] = useState(false);
  const navigate = useNavigate();
  const [msgClient, setMsgClient] = useState(null);
  const [msgChannel, setMsgChannel] = useState('sms');
  const [msgBody, setMsgBody] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const [twilioStatus, setTwilioStatus] = useState({ configured: false });
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Paginated / sortable / filterable clients table state
  const clientsTable = usePagedTable({ rows: clients, defaultPageSize: 25, defaultSort: { key: 'name', dir: 'asc' } });

  // "Add a policy now?" toggle in Add Client form
  const [addPolicyToggle, setAddPolicyToggle] = useState(false);

  // Multi-select + bulk actions (Item 10, 11)
  const [selectedIds, setSelectedIds] = useState({});
  const [bulkOpenType, setBulkOpenType] = useState(''); // '', 'whatsapp', 'sms', 'task'
  const [bulkChannel, setBulkChannel] = useState('whatsapp');
  const [bulkBody, setBulkBody] = useState('');
  const [bulkTaskTitle, setBulkTaskTitle] = useState('');
  const [bulkTaskDue, setBulkTaskDue] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsgResult, setBulkMsgResult] = useState(null);

  const selectedClients = useMemo(() => clients.filter(c => selectedIds[c.client_id]), [clients, selectedIds]);
  const selectedCount = selectedClients.length;

  const runBulkMessage = async () => {
    if (!bulkBody.trim()) { toast.error('Message body cannot be empty'); return; }
    setBulkBusy(true); setBulkMsgResult(null);
    try {
      const { data } = await axios.post(`${API}/messages/bulk-send`, {
        channel: bulkChannel,
        client_ids: selectedClients.map(c => c.client_id),
        body: bulkBody,
      }, { withCredentials: true });
      setBulkMsgResult(data);
      const summary = `${data.sent} sent · ${data.failed} failed · ${data.skipped} skipped`;
      if (data.sent > 0) toast.success(summary); else toast.warning(summary);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk send failed');
    } finally { setBulkBusy(false); }
  };

  const runBulkCreateTasks = async () => {
    if (!bulkTaskTitle.trim()) { toast.error('Task title required'); return; }
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const c of selectedClients) {
      try {
        await axios.post(`${API}/tasks`, {
          title: `${bulkTaskTitle} · ${c.name}`,
          description: `Follow-up for ${c.name} (${c.primary_mobile})`,
          due_date: bulkTaskDue || null,
          priority: 'Medium', status: 'Pending',
          client_id: c.client_id,
        }, { withCredentials: true });
        ok += 1;
      } catch { fail += 1; }
    }
    setBulkBusy(false);
    if (ok > 0) toast.success(`Created ${ok} task${ok === 1 ? '' : 's'}`);
    if (fail > 0) toast.warning(`${fail} failed`);
    setBulkOpenType(''); setBulkTaskTitle(''); setBulkTaskDue('');
  };

  const runBulkExportCsv = () => {
    const header = ['Customer ID', 'Name', 'Mobile', 'Email', 'PAN', 'Client Type', 'Created'];
    const rows = selectedClients.map(c => [c.customer_code || '', c.name, c.primary_mobile, c.email || '', c.pan || '', c.client_type || '', (c.created_at || '').slice(0, 10)]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `clients_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success(`Exported ${selectedCount} client${selectedCount === 1 ? '' : 's'}`);
  };

  // Full-page "Download Customers" — exports every client currently matching
  // the active filter (same source `clientsTable` paginates), including the
  // customer_id column needed to map rows during bulk policy upload.
  //
  // Why not reuse `runBulkExportCsv`? That one only exports *checked* rows
  // from the bulk-action toolbar. The agent needs a one-click export of the
  // whole filtered view without having to tick every checkbox first.
  const runFullCustomersExport = () => {
    const source = clientsTable?.sorted || clientsTable?.filtered || clients;
    if (!source || source.length === 0) { toast.error('No clients to export'); return; }
    const header = ['Customer ID', 'Internal ID', 'Name', 'Mobile', 'Email', 'Client Type', 'Family Members', 'Created'];
    const rows = source.map(c => [
      c.customer_code || '',
      c.client_id || '',
      c.name || '',
      c.primary_mobile || '',
      c.email || '',
      c.client_type || '',
      String(familyCounts[c.client_id] || 0),
      (c.created_at || '').slice(0, 10),
    ]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `customers_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success(`Exported ${source.length} client${source.length === 1 ? '' : 's'} with Customer IDs`);
  };

  const { openAddPolicy, onPolicyCreated, onClientCreated } = useGlobalDialogs();

  // Reload when a client or a policy is created anywhere in the app
  useEffect(() => onClientCreated(() => { loadClients(); }), [onClientCreated]);
  useEffect(() => onPolicyCreated(() => { loadClients(); }), [onPolicyCreated]);

  // Memoised template list filtered by the active channel for the Send Message dialog
  const channelTemplates = useMemo(
    () => templates.filter(t => t.channel === 'both' || t.channel === msgChannel),
    [templates, msgChannel]
  );

  useEffect(() => {
    axios.get(`${API}/messages/twilio-status`, { withCredentials: true })
      .then(r => setTwilioStatus(r.data))
      .catch(err => console.warn('Twilio status check failed:', err?.message));
    axios.get(`${API}/message-templates`, { withCredentials: true })
      .then(r => setTemplates(r.data))
      .catch(err => console.warn('Templates load failed:', err?.message));
    // Family counts for the Download Customers CSV — one aggregation, cheap.
    axios.get(`${API}/clients/family-counts`, { withCredentials: true })
      .then(r => setFamilyCounts(r.data?.counts || {}))
      .catch(err => console.warn('Family counts load failed:', err?.message));
  }, []);

  const openMessage = (c, channel) => {
    setMsgClient(c);
    setMsgChannel(channel);
    setSelectedTemplateId('');
    setMsgBody(`Hi ${c.name}, this is your agent from InsureGrowth. `);
    setMsgOpen(true);
    // Load recent message history + delivery status for this client
    axios.get(`${API}/messages?client_id=${c.client_id}&limit=10`, { withCredentials: true })
      .then((r) => setMsgHistory(r.data || []))
      .catch(() => setMsgHistory([]));
  };

  const refreshMsgHistory = async () => {
    if (!msgClient?.client_id) return;
    try {
      const { data } = await axios.get(`${API}/messages?client_id=${msgClient.client_id}&limit=10`, { withCredentials: true });
      setMsgHistory(data || []);
    } catch (e) { /* ignore */ }
  };

  const applyTemplate = async (templateId) => {
    if (!templateId) { setMsgBody(''); return; }
    setSelectedTemplateId(templateId);
    try {
      // Try to render with the latest active policy for this client as context (optional)
      let policyId = null;
      try {
        const pRes = await axios.get(`${API}/policies?client_id=${msgClient.client_id}&status=Active`, { withCredentials: true });
        if (pRes.data && pRes.data.length > 0) {
          const sorted = [...pRes.data].sort((a, b) => new Date(a.next_due_date || 0) - new Date(b.next_due_date || 0));
          policyId = sorted[0].policy_id;
        }
      } catch (err) {
        console.warn('Could not fetch active policy for template context:', err?.message);
      }
      const { data } = await axios.post(`${API}/message-templates/render`, {
        template_id: templateId,
        client_id: msgClient.client_id,
        policy_id: policyId,
      }, { withCredentials: true });
      setMsgBody(data.rendered);
    } catch (e) {
      toast.error('Failed to render template');
    }
  };

  const sendMessage = async () => {
    if (!msgBody.trim()) { toast.error('Message body cannot be empty'); return; }
    setMsgSending(true);
    try {
      await axios.post(`${API}/messages/send`, {
        to: msgClient.primary_mobile,
        body: msgBody,
        channel: msgChannel,
        client_id: msgClient.client_id,
      }, { withCredentials: true });
      toast.success(`${msgChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'} sent to ${msgClient.name}`);
      setMsgOpen(false);
      refreshMsgHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setMsgSending(false); }
  };

  const loadClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(data);
    } catch (e) {
      console.error('Failed to load clients:', e);
      toast.error(e.response?.data?.detail || 'Failed to load clients');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  // Round 44 · Item 3: ?edit={client_id} deep-link.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || clients.length === 0) return;
    const match = clients.find(c => c.client_id === editId);
    if (match) {
      openEditDialog(match);
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const mv = validateIndianMobile(formData.primary_mobile);
    if (!mv.ok) { toast.error(mv.error); return; }
    try {
      let newClientId = null;
      const payload = { ...formData, primary_mobile: mv.digits };
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.client_id}`, payload, { withCredentials: true });
        toast.success('Client updated');
      } else {
        const { data } = await axios.post(`${API}/clients`, payload, { withCredentials: true });
        newClientId = data?.client_id;
        toast.success('Client created');
        // Upload any KYC docs the agent staged — parity with the header's Add Client dialog.
        if (createKycFiles.length > 0 && newClientId) {
          setCreateKycUploading(true);
          let ok = 0, fail = 0;
          for (const item of createKycFiles) {
            try {
              const fd = new FormData();
              fd.append('file', item.file);
              await axios.post(`${API}/clients/${newClientId}/kyc?doc_type=${encodeURIComponent(item.doc_type)}`, fd, {
                withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
              });
              ok += 1;
            } catch { fail += 1; }
          }
          setCreateKycUploading(false);
          if (ok > 0) toast.success(`${ok} KYC document${ok === 1 ? '' : 's'} attached`);
          if (fail > 0) toast.warning(`${fail} document${fail === 1 ? '' : 's'} failed — retry from the Manage dialog`);
          setCreateKycFiles([]);
        }
      }
      setDialogOpen(false);
      resetForm();
      await loadClients();
      // If the agent asked to add a policy now, launch policy dialog pre-filled
      if (newClientId && addPolicyToggle) {
        openAddPolicy({ clientId: newClientId });
        setAddPolicyToggle(false);
      }
    } catch (e) {
      console.error('Save client failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to save client');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await axios.delete(`${API}/clients/${id}`, { withCredentials: true });
      toast.success('Deleted');
      loadClients();
    } catch (e) {
      console.error('Delete client failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // Edit uses the shared EditClientDialog (duplicate-warning aware).
  const openEditDialog = (c) => setEditTarget(c);

  const resetForm = () => {
    setEditingClient(null);
    setFormData({ name: '', dob: '', date_of_marriage: '', gender: 'Male', pan: '', aadhaar: '', address: '', primary_mobile: '', secondary_mobile: '', email: '', nominee: '', client_type: 'Individual',
      financial_holdings: { mutual_funds: 'unknown', loans: 'unknown', fd: 'unknown', rd: 'unknown', post_office: 'unknown', ppf: 'unknown' } });
  };

  const uploadBulk = async () => {
    if (!bulkFile) { toast.error('Please choose an Excel file'); return; }
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      const { data } = await axios.post(`${API}/clients/bulk-upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBulkResult(data);
      const errCount = (data.errors || []).length;
      if (data.inserted > 0 && errCount === 0) {
        toast.success(`Imported ${data.inserted} of ${data.total_rows} client(s)`);
      } else if (data.inserted > 0) {
        toast.warning(`Imported ${data.inserted} · ${errCount} row(s) had issues — see details below.`);
      } else {
        toast.error(`No rows imported — ${errCount} error(s). See details below.`);
      }
      // Round 46 · Item 2: 30-second Undo affordance. Only shown when at
      // least one row landed, so an all-errors upload doesn't offer an undo
      // for nothing.
      if (data.batch_id && data.inserted > 0) {
        toast('Import committed', {
          description: `${data.inserted} new client(s) added. You can undo this batch for the next 30 seconds.`,
          duration: 30000,
          action: {
            label: 'Undo import',
            onClick: async () => {
              try {
                const { data: undo } = await axios.post(
                  `${API}/client-imports/${data.batch_id}/undo`,
                  {}, { withCredentials: true },
                );
                toast.success(`Rolled back ${undo.deleted} client(s)`);
                loadClients();
              } catch { toast.error('Undo failed'); }
            },
          },
        });
      }
      loadClients();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map((d) => d?.msg || JSON.stringify(d)).join(' · ') : `HTTP ${e.response?.status || '?'} — ${e.message}`);
      setBulkResult({ inserted: 0, skipped: 0, total_rows: 0, errors: [msg] });
      toast.error(`Bulk upload failed: ${msg}`);
    } finally { setBulkLoading(false); }
  };

  const openManage = async (c) => {
    setManageClient(c);
    setManageOpen(true);
    await refreshManage(c.client_id);
  };

  const refreshManage = async (clientId) => {
    try {
      const [f, k, c] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/family`, { withCredentials: true }),
        axios.get(`${API}/clients/${clientId}/kyc`, { withCredentials: true }),
        axios.get(`${API}/clients/${clientId}/conversation`, { withCredentials: true })
      ]);
      setFamilyList(f.data);
      setKycList(k.data);
      setConversation(c.data.messages || []);
    } catch (e) {
      console.error('Failed to load client details:', e);
      toast.error(e.response?.data?.detail || 'Failed to load client details');
    }
  };

  const addFamily = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/clients/${manageClient.client_id}/family`, familyForm, { withCredentials: true });
      toast.success('Family member added');
      setFamilyForm({ name: '', relationship: 'Spouse', dob: '', gender: 'Male' });
      refreshManage(manageClient.client_id);
    } catch (e) {
      console.error('Add family failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to add family member');
    }
  };

  const deleteFamily = async (fid) => {
    if (!window.confirm('Remove this family member?')) return;
    try {
      await axios.delete(`${API}/clients/${manageClient.client_id}/family/${fid}`, { withCredentials: true });
      toast.success('Removed');
      refreshManage(manageClient.client_id);
    } catch (e) {
      console.error('Delete family failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const uploadKyc = async () => {
    if (!kycFile) { toast.error('Please choose a file'); return; }
    try {
      const fd = new FormData();
      fd.append('file', kycFile);
      await axios.post(`${API}/clients/${manageClient.client_id}/kyc?doc_type=${encodeURIComponent(kycDocType)}`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded');
      setKycFile(null);
      refreshManage(manageClient.client_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  const deleteKyc = async (kid) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/clients/${manageClient.client_id}/kyc/${kid}`, { withCredentials: true });
      toast.success('Deleted');
      refreshManage(manageClient.client_id);
    } catch (e) {
      console.error('Delete KYC failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to delete document');
    }
  };

  const downloadKyc = async (kid, filename) => {
    try {
      const res = await axios.get(`${API}/kyc/${kid}/download`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'document';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download KYC failed:', e);
      toast.error('Download failed');
    }
  };

  const sendReply = async () => {
    if (!replyBody.trim() || !manageClient) return;
    setReplySending(true);
    try {
      await axios.post(`${API}/messages/send`, {
        to: manageClient.primary_mobile,
        body: replyBody,
        channel: replyChannel,
        client_id: manageClient.client_id,
      }, { withCredentials: true });
      toast.success(`${replyChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'} sent`);
      setReplyBody('');
      refreshManage(manageClient.client_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
    } finally { setReplySending(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner"></div></div>;

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Clients</h1>
          <p className="text-slate-600 mt-1">Manage your client database, family links, and KYC documents</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setDocExtractOpen(true)} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="extract-from-doc-button">
            <Sparkles className="h-4 w-4 mr-2" /> Extract from Document
          </Button>
          <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkFile(null); setBulkResult(null); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="bulk-upload-button">
                <Upload className="h-4 w-4 mr-2" /> Excel Bulk Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>Bulk Upload Clients</DialogTitle>
                <p className="text-xs text-slate-500 mt-1">Every uploaded row auto-receives a sequential Customer ID (e.g. <code className="bg-slate-100 px-1 rounded">IG-000042</code>) — the same format as manually-added clients. Reference these IDs in the Policy bulk template to link products safely.</p>
                <DialogDescription>
                  Upload an .xlsx file. Required columns: <code>name</code>, <code>primary_mobile</code>. Optional: dob, date_of_marriage, gender, pan, aadhaar, address, secondary_mobile, email, nominee, client_type.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-2 rounded-lg border border-slate-200 bg-slate-50">
                  <span className="text-xs text-slate-600">Need the column structure?</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      const res = await downloadFile('/templates/client.xlsx', 'client_template.xlsx');
                      if (!res.ok) {
                        toast.error(`Template download failed: ${res.error}`);
                      } else {
                        toast.success('Client template downloaded');
                      }
                    }}
                    data-testid="clients-download-template"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" /> Download Template
                  </Button>
                </div>
                <Input type="file" accept=".xlsx,.xls" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} data-testid="bulk-file-input" />
                {bulkResult && (
                  <div className="p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid="clients-bulk-result">
                    <p className="text-sm">
                      <strong>Inserted:</strong> {bulkResult.inserted} · <strong>Skipped:</strong> {bulkResult.skipped ?? 0} · <strong>Total:</strong> {bulkResult.total_rows}
                    </p>
                    {bulkResult.errors?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-red-700">Errors:</p>
                        <ul className="text-xs text-red-600 mt-1 space-y-1 max-h-32 overflow-y-auto" data-testid="clients-bulk-errors">
                          {bulkResult.errors.map((e) => <li key={e}>• {e}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setBulkOpen(false)}>Close</Button>
                  <Button onClick={uploadBulk} disabled={bulkLoading || !bulkFile} className="bg-blue-800 hover:bg-blue-900" data-testid="bulk-submit-button">
                    {bulkLoading ? 'Uploading...' : 'Import Clients'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <EditClientDialog open={!!editTarget} onOpenChange={(o) => { if (!o) setEditTarget(null); }} client={editTarget} onSaved={loadClients} />
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-client-button">
                <Plus className="h-4 w-4 mr-2" /> Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
                <DialogDescription>Fill in the client details below</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Client Type</Label>
                    <Select value={formData.client_type} onValueChange={(v) => setFormData({...formData, client_type: v})}>
                      <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Individual">Individual</SelectItem>
                        <SelectItem value="Corporate">Corporate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2"><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="client-name-input" /></div>
                  <div className="space-y-2"><Label>Date of Birth</Label><Input type="date" value={formData.dob} onChange={(e) => setFormData({...formData, dob: e.target.value})} data-testid="client-dob-input" /></div>
                  <div className="space-y-2"><Label>Date of Marriage</Label><Input type="date" value={formData.date_of_marriage} onChange={(e) => setFormData({...formData, date_of_marriage: e.target.value})} data-testid="client-dom-input" /></div>
                  <div className="space-y-2"><Label>Gender</Label>
                    <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                      <SelectTrigger data-testid="client-gender-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Primary Mobile *</Label><Input inputMode="numeric" maxLength={10} placeholder="10-digit mobile" value={formData.primary_mobile} onChange={(e) => setFormData({...formData, primary_mobile: e.target.value.replace(/\D/g,'').slice(0,10)})} required data-testid="client-mobile-input" /><p className="text-[10px] text-slate-500">Exactly 10 digits, starts with 6-9</p></div>
                  <div className="space-y-2"><Label>Secondary Mobile</Label><Input value={formData.secondary_mobile} onChange={(e) => setFormData({...formData, secondary_mobile: e.target.value})} data-testid="client-secondary-mobile-input" /></div>
                  <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="client-email-input" /></div>
                  <div className="space-y-2"><Label>PAN</Label><Input value={formData.pan} onChange={(e) => setFormData({...formData, pan: e.target.value.toUpperCase()})} maxLength={10} data-testid="client-pan-input" /></div>
                  <div className="space-y-2"><Label>Aadhaar</Label><Input value={formData.aadhaar} onChange={(e) => setFormData({...formData, aadhaar: e.target.value})} maxLength={12} data-testid="client-aadhaar-input" /></div>
                  <div className="col-span-2 space-y-2"><Label>Address</Label><Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} data-testid="client-address-input" /></div>
                  <div className="col-span-2 space-y-2"><Label>Nominee</Label><Input value={formData.nominee} onChange={(e) => setFormData({...formData, nominee: e.target.value})} data-testid="client-nominee-input" /></div>
                </div>

                {/* Other Financial Holdings — quick Yes/No/Unknown tags per category.
                    Opportunity Engine only flags "no" — "unknown" never generates suggestions. */}
                <details className="rounded-lg border border-slate-200 bg-slate-50/60 open:bg-white" data-testid="financial-holdings-section">
                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-800 select-none">
                    Other Financial Holdings <span className="text-xs text-slate-500 font-normal">— quick tags so we don't cross-sell what they already have</span>
                  </summary>
                  <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      ['mutual_funds', 'Mutual Funds'],
                      ['loans', 'Loans'],
                      ['fd', 'Fixed Deposit'],
                      ['rd', 'Recurring Deposit'],
                      ['post_office', 'Post Office Schemes'],
                      ['ppf', 'PPF'],
                    ].map(([key, label]) => (
                      <div key={key} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-slate-200 bg-white">
                        <span className="text-sm text-slate-800">{label}</span>
                        <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" role="radiogroup" aria-label={label}>
                          {['yes','no','unknown'].map((v) => {
                            const active = (formData.financial_holdings?.[key] || 'unknown') === v;
                            const cls = active
                              ? (v === 'yes' ? 'bg-emerald-600 text-white' : v === 'no' ? 'bg-orange-600 text-white' : 'bg-slate-500 text-white')
                              : 'text-slate-600 hover:bg-slate-100';
                            return (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setFormData({ ...formData, financial_holdings: { ...formData.financial_holdings, [key]: v } })}
                                className={`px-2 py-0.5 text-[11px] rounded font-medium capitalize ${cls}`}
                                data-testid={`holding-${key}-${v}`}
                              >
                                {v === 'unknown' ? '?' : v}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="px-3 pb-2 text-[11px] text-slate-500">Only <strong>No</strong> triggers a cross-sell suggestion — <strong>?</strong> (unknown) is silent.</p>
                </details>

                {/* KYC Documents (create-mode only) — parity with the header Add Client dialog. */}
                {!editingClient && (
                  <details className="rounded-lg border border-blue-100 bg-blue-50/40 open:bg-white" data-testid="client-create-kyc-section">
                    <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-800 select-none">
                      KYC Documents <span className="text-xs text-slate-500 font-normal">(optional — upload PAN / Aadhaar / photo)</span>
                    </summary>
                    <div className="p-3 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Select value={kycDocType} onValueChange={setKycDocType}>
                          <SelectTrigger className="w-40" data-testid="client-create-kyc-doc-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['Aadhaar','PAN','Passport','Driving Licence','Voter ID','Photo','Other'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" multiple onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length === 0) return;
                          setCreateKycFiles(prev => [...prev, ...files.map(f => ({ file: f, doc_type: kycDocType }))]);
                          e.target.value = '';
                        }} data-testid="client-create-kyc-file-input" className="flex-1 min-w-[200px]" />
                      </div>
                      {createKycFiles.length > 0 && (
                        <ul className="divide-y divide-slate-100 border border-slate-200 rounded" data-testid="client-create-kyc-list">
                          {createKycFiles.map((k, i) => (
                            <li key={`${i}-${k.file.name}`} className="flex items-center justify-between gap-2 px-2 py-1.5 text-xs">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <Badge className="bg-slate-100 text-slate-700">{k.doc_type}</Badge>
                                <span className="truncate">{k.file.name}</span>
                                <span className="text-slate-400">({Math.round(k.file.size / 1024)} KB)</span>
                              </div>
                              <button type="button" onClick={() => setCreateKycFiles(prev => prev.filter((_, j) => j !== i))} className="text-red-600 hover:text-red-800 text-[11px]" data-testid={`client-create-kyc-remove-${i}`}>Remove</button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </details>
                )}

                {/* Optional inline policy add — only show when creating (not editing) */}
                {!editingClient && (
                  <label className="flex items-start gap-3 p-3 rounded-lg border border-indigo-100 bg-indigo-50/40 cursor-pointer select-none" data-testid="add-policy-toggle-label">
                    <input
                      type="checkbox"
                      checked={addPolicyToggle}
                      onChange={(e) => setAddPolicyToggle(e.target.checked)}
                      className="mt-1 h-4 w-4 accent-blue-800"
                      data-testid="add-policy-toggle"
                    />
                    <span className="text-sm text-slate-700">
                      <span className="font-medium text-slate-900">Add a policy now?</span>
                      <span className="block text-xs text-slate-500 mt-0.5">
                        We'll open the Add Policy form pre-filled with this client right after you save.
                      </span>
                    </span>
                  </label>
                )}

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-blue-800 hover:bg-blue-900" data-testid="client-submit-button">{editingClient ? 'Update' : 'Create'} Client</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
          <CardTitle style={{ fontFamily: 'Manrope' }}>All Clients ({clients.length})</CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={runFullCustomersExport}
            className="border-blue-700 text-blue-800 hover:bg-blue-50"
            data-testid="clients-download-customers"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Download Customers
          </Button>
        </CardHeader>
        <CardContent>
          {/* Bulk-action toolbar (Items 10, 11) */}
          {selectedCount > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid="clients-bulk-bar">
              <span className="text-sm font-medium text-blue-900">{selectedCount} selected</span>
              <div className="ml-auto flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => { setBulkChannel('whatsapp'); setBulkBody(''); setBulkMsgResult(null); setBulkOpenType('whatsapp'); }} data-testid="clients-bulk-whatsapp" className="border-green-600 text-green-700 hover:bg-green-50">
                  <MessageSquare className="h-3.5 w-3.5 mr-1" /> Bulk WhatsApp
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setBulkChannel('sms'); setBulkBody(''); setBulkMsgResult(null); setBulkOpenType('sms'); }} data-testid="clients-bulk-sms">
                  <Send className="h-3.5 w-3.5 mr-1" /> Bulk SMS
                </Button>
                <Button size="sm" variant="outline" onClick={() => setBulkOpenType('task')} data-testid="clients-bulk-task">
                  <ClipboardList className="h-3.5 w-3.5 mr-1" /> Create Tasks
                </Button>
                <Button size="sm" variant="outline" onClick={runBulkExportCsv} data-testid="clients-bulk-export">
                  <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds({})} data-testid="clients-bulk-clear">Clear</Button>
              </div>
            </div>
          )}
          {clients.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No clients yet. Add your first client or bulk upload from Excel!</p>
          ) : (<>
            {(() => null)()}
            <div className="overflow-x-auto" data-testid="clients-table-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input type="checkbox" checked={clientsTable.pageRows.length > 0 && clientsTable.pageRows.every(c => selectedIds[c.client_id])} onChange={(e) => { const n = { ...selectedIds }; clientsTable.pageRows.forEach(c => { if (e.target.checked) n[c.client_id] = true; else delete n[c.client_id]; }); setSelectedIds(n); }} data-testid="clients-select-all" className="h-4 w-4 accent-blue-800" />
                  </TableHead>
                  <TableHead className="w-12">S.No.</TableHead>
                  <TableHead>Customer ID</TableHead>
                  <TableHead><SortableTh sortKey="name" state={clientsTable}>Name</SortableTh></TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <span>Type</span>
                      <select className="text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-white" value={clientsTable.filters.client_type || 'all'} onChange={(e) => clientsTable.setFilter('client_type', e.target.value)} data-testid="filter-client-type">
                        <option value="all">All</option><option value="Individual">Individual</option><option value="Corporate">Corporate</option>
                      </select>
                    </div>
                  </TableHead>
                  <TableHead><SortableTh sortKey="primary_mobile" state={clientsTable}>Mobile</SortableTh></TableHead>
                  <TableHead>Exposure</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead><SortableTh sortKey="pan" state={clientsTable}>PAN</SortableTh></TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientsTable.pageRows.map((c, idx) => (
                  <TableRow key={c.client_id} data-testid={`client-row-${c.client_id}`}>
                    <TableCell>
                      <input type="checkbox" checked={!!selectedIds[c.client_id]} onChange={(e) => setSelectedIds(prev => { const n = { ...prev }; if (e.target.checked) n[c.client_id] = true; else delete n[c.client_id]; return n; })} data-testid={`client-select-${c.client_id}`} className="h-4 w-4 accent-blue-800" />
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{clientsTable.startIndex + idx + 1}</TableCell>
                    <TableCell className="text-xs font-mono text-slate-600">{c.customer_code || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="font-medium">
                      <button type="button" onClick={() => navigate(`/clients/${c.client_id}`)} className="text-blue-800 hover:underline text-left" data-testid={`open-client-detail-${c.client_id}`}>
                        {c.name}
                      </button>
                    </TableCell>
                    <TableCell>
                      {c.client_type === 'Corporate'
                        ? <Badge className="bg-purple-100 text-purple-800"><Building className="h-3 w-3 mr-1" />Corporate</Badge>
                        : <Badge className="bg-blue-100 text-blue-800"><User className="h-3 w-3 mr-1" />Individual</Badge>}
                    </TableCell>
                    <TableCell>{c.primary_mobile}</TableCell>
                    <TableCell><ClientExposureChart clientId={c.client_id} compact /></TableCell>
                    <TableCell>{c.email || '-'}</TableCell>
                    <TableCell>{c.pan || '-'}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {/* Round 59 · Item 5 — only WhatsApp + Add Product on the row; others into ⋯ menu. */}
                      <Button size="sm" variant="outline" onClick={() => openMessage(c, 'whatsapp')} data-testid={`whatsapp-client-${c.client_id}`} className="border-green-600 text-green-700 hover:bg-green-50 h-7 px-2 mr-1">
                        <MessageSquare className="h-3.5 w-3.5 mr-1" /> WhatsApp
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openAddPolicy({ clientId: c.client_id })} data-testid={`add-policy-client-${c.client_id}`} className="border-indigo-600 text-indigo-700 hover:bg-indigo-50 h-7 px-2 mr-1">
                        <FilePlus2 className="h-3.5 w-3.5 mr-1" /> Add Product
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" className="h-7 w-7 p-0" data-testid={`more-client-${c.client_id}`} aria-label="More actions">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => openMessage(c, 'sms')} data-testid={`sms-client-${c.client_id}`}>
                            <Phone className="h-3.5 w-3.5 mr-2" /> SMS
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openManage(c)} data-testid={`manage-client-${c.client_id}`}>
                            <Users2 className="h-3.5 w-3.5 mr-2" /> Manage
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditDialog(c)} data-testid={`edit-client-${c.client_id}`}>
                            <Edit className="h-3.5 w-3.5 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDelete(c.client_id)} className="text-red-700 focus:text-red-700" data-testid={`delete-client-${c.client_id}`}>
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            <PaginationBar state={clientsTable} testId="clients-pagination" />
          </>)}
        </CardContent>
      </Card>

      {/* Manage Dialog: Family + KYC */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle>{manageClient?.name} · Family & KYC</DialogTitle>
                <DialogDescription>Link family members and manage KYC documents</DialogDescription>
              </div>
              {manageClient && (
                <Button
                  onClick={() => openAddPolicy({ clientId: manageClient.client_id })}
                  className="bg-indigo-700 hover:bg-indigo-800 text-white shrink-0"
                  size="sm"
                  data-testid="manage-add-policy-button"
                >
                  <FilePlus2 className="h-4 w-4 mr-1.5" /> Add Policy
                </Button>
              )}
            </div>
          </DialogHeader>
          <Tabs defaultValue="family">
            <TabsList>
              <TabsTrigger value="family" data-testid="tab-family"><Users2 className="h-4 w-4 mr-2" />Family</TabsTrigger>
              <TabsTrigger value="kyc" data-testid="tab-kyc"><FileText className="h-4 w-4 mr-2" />KYC Documents</TabsTrigger>
              <TabsTrigger value="conversation" data-testid="tab-conversation"><MessagesSquare className="h-4 w-4 mr-2" />Conversation</TabsTrigger>
            </TabsList>

            <TabsContent value="family" className="mt-4 space-y-4">
              <form onSubmit={addFamily} className="grid grid-cols-2 gap-3 p-4 border border-slate-200 rounded-lg">
                <div className="space-y-1"><Label className="text-xs">Name *</Label><Input value={familyForm.name} onChange={(e) => setFamilyForm({...familyForm, name: e.target.value})} required data-testid="family-name-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Relationship</Label>
                  <Select value={familyForm.relationship} onValueChange={(v) => setFamilyForm({...familyForm, relationship: v})}>
                    <SelectTrigger data-testid="family-relationship-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Spouse">Spouse</SelectItem><SelectItem value="Son">Son</SelectItem>
                      <SelectItem value="Daughter">Daughter</SelectItem><SelectItem value="Father">Father</SelectItem>
                      <SelectItem value="Mother">Mother</SelectItem><SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">DOB</Label><Input type="date" value={familyForm.dob} onChange={(e) => setFamilyForm({...familyForm, dob: e.target.value})} data-testid="family-dob-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Gender</Label>
                  <Select value={familyForm.gender} onValueChange={(v) => setFamilyForm({...familyForm, gender: v})}>
                    <SelectTrigger data-testid="family-gender-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Button type="submit" className="bg-blue-800 hover:bg-blue-900 w-full" data-testid="family-add-button"><Plus className="h-4 w-4 mr-2" />Add Family Member</Button>
                </div>
              </form>

              {familyList.length === 0 ? (
                <p className="text-center text-slate-500 py-6 text-sm">No family members linked yet</p>
              ) : (
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Relationship</TableHead><TableHead>DOB</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {familyList.map(m => (
                      <TableRow key={m.family_id}>
                        <TableCell className="font-medium">{m.name}</TableCell>
                        <TableCell><Badge variant="outline">{m.relationship}</Badge></TableCell>
                        <TableCell>{m.dob ? formatDate(m.dob) : '-'}</TableCell>
                        <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => deleteFamily(m.family_id)}><Trash2 className="h-4 w-4 text-red-600" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="kyc" className="mt-4 space-y-4">
              <div className="grid grid-cols-3 gap-3 p-4 border border-slate-200 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-xs">Document Type</Label>
                  <Select value={kycDocType} onValueChange={setKycDocType}>
                    <SelectTrigger data-testid="kyc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aadhaar">Aadhaar</SelectItem>
                      <SelectItem value="PAN">PAN</SelectItem>
                      <SelectItem value="Photo">Photo</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">File (PDF/JPG/PNG, max 10MB)</Label>
                  <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setKycFile(e.target.files?.[0] || null)} data-testid="kyc-file-input" />
                </div>
                <div className="col-span-3">
                  <Button onClick={uploadKyc} disabled={!kycFile} className="bg-blue-800 hover:bg-blue-900 w-full" data-testid="kyc-upload-button"><FileUp className="h-4 w-4 mr-2" />Upload Document</Button>
                </div>
              </div>

              {kycList.length === 0 ? (
                <p className="text-center text-slate-500 py-6 text-sm">No KYC documents uploaded yet</p>
              ) : (
                <Table>
                  <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Filename</TableHead><TableHead>Uploaded</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {kycList.map(d => (
                      <TableRow key={d.kyc_id}>
                        <TableCell><Badge className="bg-blue-100 text-blue-800">{d.doc_type}</Badge></TableCell>
                        <TableCell className="font-medium">{d.original_filename}</TableCell>
                        <TableCell>{formatDate(d.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => downloadKyc(d.kyc_id, d.original_filename)}><Download className="h-4 w-4 text-blue-700" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteKyc(d.kyc_id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="conversation" className="mt-4 space-y-3">
              {conversation.length === 0 ? (
                <div className="text-center py-8 text-sm text-slate-500">
                  <MessagesSquare className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  No conversation yet. Send an SMS or WhatsApp below, or wait for a reply.
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto space-y-2 p-2 bg-slate-50 rounded-lg">
                  {conversation.map((m) => {
                    const isIn = m.direction === 'inbound';
                    return (
                      <div key={m.message_id} className={`flex ${isIn ? 'justify-start' : 'justify-end'}`}>
                        <div className={`max-w-[75%] p-3 rounded-lg ${isIn ? 'bg-white border border-slate-200' : 'bg-blue-800 text-white'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs uppercase tracking-wider font-medium ${isIn ? 'text-slate-500' : 'text-blue-200'}`}>
                              {m.channel} · {isIn ? 'inbound' : 'outbound'}
                            </span>
                            {m.status && !isIn && (
                              <span className={`text-[10px] uppercase ${m.status === 'sent' ? 'text-blue-200' : 'text-amber-200'}`}>
                                {m.status}
                              </span>
                            )}
                          </div>
                          <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                          <p className={`text-[10px] mt-1 ${isIn ? 'text-slate-400' : 'text-blue-200'}`}>
                            {new Date(m.sent_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Inline reply compose */}
              <div className="border border-slate-200 rounded-lg p-3 bg-white space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Reply via</Label>
                  <Select value={replyChannel} onValueChange={setReplyChannel}>
                    <SelectTrigger className="w-32 h-8" data-testid="reply-channel-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-slate-500">to {manageClient?.primary_mobile}</span>
                </div>
                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder="Type your reply..."
                  rows={3}
                  maxLength={1500}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-800 text-sm"
                  data-testid="reply-body-input"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500">{replyBody.length} / 1500</p>
                  <Button
                    onClick={sendReply}
                    disabled={replySending || !replyBody.trim() || replyBody.length > 1500}
                    className="bg-blue-800 hover:bg-blue-900"
                    size="sm"
                    data-testid="reply-send-button"
                  >
                    <Send className="h-3 w-3 mr-1" />
                    {replySending ? 'Sending...' : `Send ${replyChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'}`}
                  </Button>
                </div>
              </div>

              <p className="text-xs text-slate-500">
                Inbound replies land here automatically once your Twilio Messaging webhook is pointed at <code className="break-all">{`${process.env.REACT_APP_BACKEND_URL}/api/twilio/webhook`}</code>.
              </p>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
      {/* Send Message Dialog */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Send {msgChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'} to {msgClient?.name}
            </DialogTitle>
            <DialogDescription>
              Sending to {msgClient?.primary_mobile}
              {!twilioStatus.configured && (
                <span className="block mt-2 text-amber-700 text-sm">
                  ⚠ Twilio credentials are placeholders. Add real TWILIO_ACCOUNT_SID and TWILIO_AUTH_TOKEN to <code>backend/.env</code> to deliver messages.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Load Template</Label>
              <Select value={selectedTemplateId} onValueChange={applyTemplate}>
                <SelectTrigger data-testid="message-template-select">
                  <SelectValue placeholder="-- Choose a template --" />
                </SelectTrigger>
                <SelectContent>
                  {channelTemplates.map(t => (
                    <SelectItem key={t.template_id} value={t.template_id}>
                      {t.name} <span className="text-xs text-slate-500 ml-2">· {t.category}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Templates auto-fill client name, policy number, expiry date, etc.</p>
            </div>
            <div>
              <Label>Message</Label>
              <textarea
                value={msgBody}
                onChange={(e) => setMsgBody(e.target.value)}
                rows={5}
                maxLength={1500}
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-800"
                data-testid="message-body-input"
              />
              <p className="text-xs text-slate-500 mt-1">{msgBody.length} / 1500</p>
            </div>
            {msgHistory.length > 0 && (
              <div data-testid="msg-history-panel">
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-sm">Recent Messages · Delivery Status</Label>
                  <button
                    type="button"
                    onClick={refreshMsgHistory}
                    className="text-[10px] text-blue-800 hover:underline"
                    data-testid="msg-history-refresh"
                  >
                    Refresh
                  </button>
                </div>
                <ul className="max-h-40 overflow-y-auto space-y-1 rounded-md border border-slate-200 bg-slate-50 p-2">
                  {msgHistory.map((m) => {
                    const status = (m.delivery_status || m.status || '').toLowerCase();
                    const tone =
                      status === 'delivered' || status === 'read' ? 'bg-green-100 text-green-800' :
                      status === 'failed' || status === 'undelivered' ? 'bg-red-100 text-red-800' :
                      status === 'sent' || status === 'queued' ? 'bg-blue-100 text-blue-800' :
                      'bg-slate-100 text-slate-700';
                    return (
                      <li key={m.message_id} className="flex items-start justify-between gap-2 text-[11px]" data-testid={`msg-history-row-${m.message_id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-slate-800 truncate">{m.body}</p>
                          <p className="text-[9px] text-slate-500 uppercase tracking-wider">{m.channel} · {m.direction}</p>
                        </div>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${tone} shrink-0`}>
                          {status || 'pending'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setMsgOpen(false)}>Cancel</Button>
              <Button onClick={sendMessage} disabled={msgSending} className="bg-blue-800 hover:bg-blue-900" data-testid="message-send-button">
                {msgSending ? 'Sending...' : `Send ${msgChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <DocumentExtractDialog
        open={docExtractOpen}
        onClose={() => setDocExtractOpen(false)}
        clients={clients}
        onSaved={() => loadClients()}
      />
      <ExtractionAccuracyPanel />

      {/* Bulk WhatsApp / SMS dialog (Item 10) */}
      <Dialog open={bulkOpenType === 'whatsapp' || bulkOpenType === 'sms'} onOpenChange={(o) => { if (!o) setBulkOpenType(''); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Bulk {bulkChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'} · {selectedCount} client{selectedCount === 1 ? '' : 's'}</DialogTitle>
            <DialogDescription>Same message sent to every selected client. Use {'{{client_name}}'} to personalise.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <textarea value={bulkBody} onChange={(e) => setBulkBody(e.target.value)} rows={5} maxLength={1500} placeholder="Hi {{client_name}}, this is your agent from InsureGrowth…" className="w-full px-3 py-2 border border-slate-300 rounded-md" data-testid="clients-bulk-body" />
            {bulkMsgResult && (
              <p className="text-sm text-slate-700">Result: <span className="text-green-700">{bulkMsgResult.sent} sent</span> · <span className="text-red-700">{bulkMsgResult.failed} failed</span> · {bulkMsgResult.skipped} skipped</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkOpenType('')}>Close</Button>
              <Button onClick={runBulkMessage} disabled={bulkBusy || !bulkBody.trim()} className="bg-blue-800 hover:bg-blue-900" data-testid="clients-bulk-send">
                {bulkBusy ? 'Sending…' : `Send to ${selectedCount}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk task (Item 11) */}
      <Dialog open={bulkOpenType === 'task'} onOpenChange={(o) => { if (!o) setBulkOpenType(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create task for {selectedCount} client{selectedCount === 1 ? '' : 's'}</DialogTitle>
            <DialogDescription>One task per client. Client name is appended to the title.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Task Title *</Label>
              <Input value={bulkTaskTitle} onChange={(e) => setBulkTaskTitle(e.target.value)} placeholder="e.g. Call for renewal quote" data-testid="clients-bulk-task-title" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Due Date</Label>
              <Input type="date" value={bulkTaskDue} onChange={(e) => setBulkTaskDue(e.target.value)} data-testid="clients-bulk-task-due" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkOpenType('')}>Cancel</Button>
              <Button onClick={runBulkCreateTasks} disabled={bulkBusy || !bulkTaskTitle.trim()} className="bg-blue-800 hover:bg-blue-900" data-testid="clients-bulk-task-apply">
                {bulkBusy ? 'Creating…' : `Create ${selectedCount} task${selectedCount === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
